import * as React from "react";
import { ExternalLink, Newspaper, Clock, ChevronDown, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { GameReels } from "./GameReels";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const NEWS_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes

export const GameNews = () => {
  const [news, setNews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const [newsPage, setNewsPage] = React.useState(0);
  const [lastUpdate, setLastUpdate] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Fetch news function
  const fetchNews = React.useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setIsRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/news?limit=50&page=1&_t=${Date.now()}`);
      const data = await response.json();
      setNews(data.news || []);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error("Error fetching news:", err);
      if (!news.length) setError("Haberler yüklenemedi");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [news.length]);

  // Initial fetch
  React.useEffect(() => {
    fetchNews();
  }, []);

  // Auto-refresh every 2 minutes
  React.useEffect(() => {
    const interval = setInterval(() => {
      fetchNews(true);
    }, NEWS_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchNews]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffHours < 1) return "Az önce";
      if (diffHours < 24) return `${diffHours}s`;
      if (diffHours < 48) return "Dün";
      
      return date.toLocaleDateString('tr-TR', { 
        day: 'numeric', 
        month: 'short' 
      });
    } catch {
      return "";
    }
  };

  // Default placeholder image
  const getImage = (item, index) => {
    if (item.image) return item.image;
    return `https://picsum.photos/seed/${encodeURIComponent(item.title || index)}/200/200`;
  };

  // Get source color
  const getSourceColor = (source) => {
    const lowerSource = source?.toLowerCase() || '';
    
    if (lowerSource.includes('google')) return 'bg-blue-500/80';
    if (lowerSource.includes('webtekno')) return 'bg-blue-600/80';
    if (lowerSource.includes('technopat')) return 'bg-green-500/80';
    if (lowerSource.includes('shiftdelete')) return 'bg-red-500/80';
    if (lowerSource.includes('ign')) return 'bg-red-600/80';
    if (lowerSource.includes('tamindir')) return 'bg-orange-500/80';
    if (lowerSource.includes('turkmmo')) return 'bg-purple-500/80';
    if (lowerSource.includes('donanım') || lowerSource.includes('donanim')) return 'bg-cyan-500/80';
    
    return 'bg-gray-500/80';
  };

  // Clean source name
  const cleanSourceName = (source) => {
    if (!source) return 'Haber';
    if (source.includes('Google')) return 'Google';
    const name = source.split(' ')[0].split('.')[0];
    if (name.length > 8) return name.substring(0, 6) + '..';
    return name;
  };

  // Square news card renderer
  const renderSquareNewsCard = (item, index) => (
    <a
      key={`news-${index}-${item.title?.substring(0, 20)}`}
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
      data-testid={`news-card-${index}`}
    >
      <Card className="overflow-hidden border-white/10 bg-white/5 shadow-[0_4px_30px_rgba(0,0,0,0.1)] transition-all duration-300 hover:border-primary/50 hover:shadow-[0_8px_40px_rgba(168,85,247,0.15)] hover:scale-[1.02] aspect-square">
        {/* Image - full card */}
        <div className="relative w-full h-full overflow-hidden">
          <img
            src={getImage(item, index)}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            onError={(e) => {
              e.target.src = `https://picsum.photos/seed/${index}/200/200`;
            }}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          
          {/* Source badge */}
          <Badge 
            className={`absolute top-1.5 left-1.5 ${getSourceColor(item.source)} text-white border-0 text-[8px] px-1.5 py-0.5`}
          >
            {cleanSourceName(item.source)}
          </Badge>
          
          {/* External link icon */}
          <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLink className="size-3 text-white drop-shadow-lg" />
          </div>
          
          {/* Title at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-2">
            <h3 className="font-medium text-[10px] sm:text-[11px] line-clamp-2 text-white group-hover:text-primary transition-colors leading-tight drop-shadow-lg">
              {item.title}
            </h3>
            {item.published && (
              <div className="flex items-center gap-1 mt-1 text-[8px] text-white/70">
                <Clock className="size-2" />
                {formatDate(item.published)}
              </div>
            )}
          </div>
        </div>
      </Card>
    </a>
  );

  // Regular news card for first row
  const renderNewsCard = (item, index) => (
    <a
      key={`news-${index}-${item.title?.substring(0, 20)}`}
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
      data-testid={`news-card-${index}`}
    >
      <Card className="overflow-hidden h-full border-white/10 bg-white/5 shadow-[0_4px_30px_rgba(0,0,0,0.1)] transition-all duration-300 hover:border-primary/50 hover:shadow-[0_8px_40px_rgba(168,85,247,0.15)] hover:scale-[1.02]">
        {/* Image */}
        <div className="relative h-24 sm:h-28 overflow-hidden">
          <img
            src={getImage(item, index)}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            onError={(e) => {
              e.target.src = `https://picsum.photos/seed/${index}/300/170`;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          
          {/* Source badge */}
          <Badge 
            className={`absolute top-1.5 left-1.5 ${getSourceColor(item.source)} text-white border-0 text-[8px] sm:text-[9px] px-1.5 py-0.5`}
          >
            {cleanSourceName(item.source)}
          </Badge>
          
          {/* External link icon */}
          <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLink className="size-3 text-white drop-shadow-lg" />
          </div>
        </div>
        
        {/* Content */}
        <div className="p-2 sm:p-2.5 bg-white/5 border-t border-white/10">
          <h3 className="font-medium text-[11px] sm:text-xs line-clamp-2 group-hover:text-primary transition-colors leading-tight">
            {item.title}
          </h3>
          
          {/* Date */}
          {item.published && (
            <div className="flex items-center gap-1 mt-1.5 text-[8px] sm:text-[9px] text-muted-foreground">
              <Clock className="size-2.5" />
              {formatDate(item.published)}
            </div>
          )}
        </div>
      </Card>
    </a>
  );

  if (loading) {
    return (
      <div className="mt-6" data-testid="news-loading">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="size-5 text-primary" />
          <h2 className="text-lg sm:text-xl font-bold">Oyun Haberleri</h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="overflow-hidden border-white/10 bg-white/5">
              <Skeleton className="h-20 sm:h-24 w-full" />
              <div className="p-2 space-y-1.5">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || news.length === 0) {
    return null;
  }

  // First row - 6 news cards (3 on mobile)
  const firstRowNews = news.slice(0, 6);
  // Remaining news for expanded view
  const remainingNews = news.slice(6);
  
  // Pagination for expanded news (6 items per page for desktop)
  const newsPerPage = 6;
  const totalPages = Math.ceil(remainingNews.length / newsPerPage);
  const currentPageNews = remainingNews.slice(newsPage * newsPerPage, (newsPage + 1) * newsPerPage);

  const handleNextNews = () => {
    setNewsPage((prev) => (prev + 1) % totalPages);
  };

  const handlePrevNews = () => {
    setNewsPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  return (
    <div className="mt-6" data-testid="game-news-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Newspaper className="size-5 text-primary" />
          <h2 className="text-lg sm:text-xl font-bold">Oyun Haberleri</h2>
          <Badge variant="secondary" className="text-[10px] gap-1">
            {isRefreshing ? (
              <RefreshCw className="size-2.5 animate-spin" />
            ) : (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
            )}
            Canlı
          </Badge>
        </div>
        {news.length > 6 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs gap-1"
            onClick={() => setExpanded(!expanded)}
            data-testid="toggle-news-btn"
          >
            {expanded ? "Gizle" : "Tümünü Gör"}
            <ChevronDown className={`size-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </Button>
        )}
      </div>
      
      {/* First Row - Always visible (3 on mobile, 6 on desktop) */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
        {firstRowNews.slice(0, 3).map((item, index) => (
          <div key={`mobile-${index}`} className="block sm:hidden">
            {renderNewsCard(item, index)}
          </div>
        ))}
        {firstRowNews.map((item, index) => (
          <div key={`desktop-${index}`} className="hidden sm:block">
            {renderNewsCard(item, index)}
          </div>
        ))}
      </div>

      {/* Expanded Section */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="expanded-news-section">
          {/* Desktop Layout - Reels + news cards */}
          <div className="hidden lg:block">
            <div className="flex gap-3 items-stretch">
              {/* Reels Container - Big */}
              <div className="w-[480px] flex-shrink-0">
                <div className="h-full min-h-[340px]">
                  <GameReels />
                </div>
              </div>
              
              {/* News grid - 3x2 = 6 cards */}
              <div className="flex-1 flex flex-col">
                <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="news-grid-desktop">
                  {currentPageNews.slice(0, 6).map((item, index) => (
                    <a
                      key={`news-${newsPage}-${index}`}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block"
                    >
                      <Card className="overflow-hidden h-full border-white/10 bg-white/5 shadow-[0_4px_30px_rgba(0,0,0,0.1)] transition-all duration-300 hover:border-primary/50 hover:shadow-[0_8px_40px_rgba(168,85,247,0.15)] hover:scale-[1.02]">
                        <div className="relative h-24 sm:h-28 overflow-hidden">
                          <img
                            src={item.image || `https://picsum.photos/seed/${item.title?.substring(0,10) || index}/300/170`}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = `https://picsum.photos/seed/${index}/300/170`;
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                          <Badge className={`absolute top-1.5 left-1.5 ${getSourceColor(item.source)} text-white border-0 text-[8px] sm:text-[9px] px-1.5 py-0.5`}>
                            {cleanSourceName(item.source)}
                          </Badge>
                          <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ExternalLink className="size-3 text-white drop-shadow-lg" />
                          </div>
                        </div>
                        <div className="p-2 sm:p-2.5 bg-white/5 border-t border-white/10">
                          <h3 className="font-medium text-[11px] sm:text-xs line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                            {item.title}
                          </h3>
                          {item.published && (
                            <div className="flex items-center gap-1 mt-1.5 text-[8px] sm:text-[9px] text-muted-foreground">
                              <Clock className="size-2.5" />
                              {formatDate(item.published)}
                            </div>
                          )}
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
                
                {/* Navigation buttons */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevNews}
                      className="h-7 text-xs gap-1"
                      data-testid="prev-news-btn"
                    >
                      <ChevronLeft className="size-3" />
                      Önceki
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {newsPage + 1} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextNews}
                      className="h-7 text-xs gap-1"
                      data-testid="next-news-btn"
                    >
                      Sonraki
                      <ChevronRight className="size-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Mobile/Tablet Layout - Reels full width, then news below */}
          <div className="lg:hidden">
            <div className="w-full h-[350px] sm:h-[400px] mb-4">
              <GameReels />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="news-grid-mobile">
              {currentPageNews.map((item, index) => renderSquareNewsCard(item, newsPage * newsPerPage + index + 6))}
            </div>
            
            {/* Mobile navigation */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevNews}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="text-xs text-muted-foreground">
                  {newsPage + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextNews}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
